import { AuthStore } from "src/stores/AuthStore";
import { ConfigSettings } from "./NetworkConfig";
import { User } from "./User";
import { v4 as uuidv4 } from "uuid";
import { setCookie } from "src/utils/Cookie";
import { Route } from "src/config/routes";

function info(_message: string) {
}

class AuthError extends Error {
    area: string;
    constructor(message: string, area: string) {
        super(message);
        this.name = "AuthError";
        this.area = area;
    }
}

const getErrorMessage = async (response: Response): Promise<string> => {
    try {
        const body = await response.json();
        if (body?.code) {
            return body.code;
        } else if (body?.error) {
            return `${body.error}: ${body.error_description}`;
        } else if (body?.value) {
            return body.value;
        } else if (body) {
            return `${body}`;
        }
        return `${response.status}: ${response.statusText}`;
    } catch (error) {
        return "screens.auth.generic_error";
    }
};

export class AuthClient {
    private config: ConfigSettings;
    private authStore: AuthStore;

    constructor(config: ConfigSettings, authStore: AuthStore) {
        this.config = config;
        this.authStore = authStore;
    }

    async initiateLogin() {
        const loginCodeVerifier = uuidv4();
        this.authStore.setLoginCodeVerifier(loginCodeVerifier);
        const encoder = new TextEncoder();
        const data = encoder.encode(loginCodeVerifier);
        const hash = await crypto.subtle.digest("SHA-256", data);
        const hashArray = new Uint8Array(hash);
        const loginCodeChallenge = btoa(String.fromCharCode(...hashArray))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=/g, "");

        const isRegisterRoute = window.location.pathname === Route.register;

        // `includes("localhost")` would also match `*.wwportal.localhost`
        // (the local-stage gateway domain), routing dev users to a bogus
        // http://localhost:3000 redirect_uri. Pin to a true "localhost" or
        // "localhost:<port>" host so only bare-localhost dev gets the alias.
        const isLocalhostHost = /^localhost(:\d+)?$/.test(window.location.host);

        let redirectUri = isLocalhostHost
            ? `http://localhost:3000${this.authStore.entryPath}`
            : this.authStore.entryUri;

        // register route does not exist in portal, solely used for redirection
        if (isRegisterRoute) {
            redirectUri = redirectUri.split(Route.register)[0];
        }

        this.authStore.setLoginRedirectUri(redirectUri);

        const subdomain = isLocalhostHost
            ? this.config.environment
            : window.location.host.split(".")[0];

        let urlInitiateLoginEndpoint = `${this.config.cidaasBaseUrl}${this.config.cidaasAuthorisationServerRessource}?client_id=${this.config.wwCustomerManagementClientId}&response_type=code&scopes=${this.authStore.scopes}&redirect_uri=${redirectUri}&code_challenge=${loginCodeChallenge}&code_challenge_method=${this.authStore.loginCodeChallengeMethod}&subdomain=${subdomain}&isCidaasRedirect=true`;

        // register route does not exist in portal, solely used for redirection
        if (isRegisterRoute && window.location.search.includes("code")) {
            const registrationCodeParam = new URLSearchParams(window.location.search).get("code");
            urlInitiateLoginEndpoint = `${urlInitiateLoginEndpoint}&registrationcode=${registrationCodeParam}`;
        }

        window.location.href = urlInitiateLoginEndpoint;
    }

    async login(loginVerificationCode: string): Promise<boolean> {
        info("loginCidaas starts");
        this.authStore.setLogin(true);

        const getCidaasAccessTokenRequestBody = {
            grant_type: "authorization_code",
            client_id: this.config.wwCustomerManagementClientId,
            code: loginVerificationCode,
            code_verifier: this.authStore.loginCodeVerifier,
            scope: this.authStore.scopes,
            redirect_uri: this.authStore.loginRedirectUri
        };

        const response = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
            },
            body: new URLSearchParams(getCidaasAccessTokenRequestBody)
        });

        info(`login response: ${response.status}: ${response.statusText}`);

        if (response.status === 200) {
            const token = await response.json();

            const user: User = new User();
            await user.init(token.access_token, token.sub, token.identity_id);

            this.authStore.addUser(user, token.access_token, token.refresh_token, token.expires_in);
            setCookie("token", token.access_token, "Strict");

            info("loginCidaas successful");
            this.authStore.setLogin(false);
            return Promise.resolve(true);
        } else {
            info("loginCidaas unsuccessful");
            this.authStore.setLogin(false);
            const body = await response.json();

            if (body?.error) {
                info(`--> ${body.error}: ${body.error_description}`);

                if (body.error_description) {
                    return Promise.reject(new AuthError(`${body.error_description}`, "login"));
                } else {
                    return Promise.reject(new AuthError(`${body.error}`, "login"));
                }
            }
            return Promise.reject(new AuthError(`${response.status}: ${response.statusText}`, "login"));
        }
    }

    async relogin(refreshToken: string | undefined): Promise<boolean> {
        if (refreshToken) {
            if (!this.authStore.relogin) {
                info("relogin start");
                this.authStore.setRelogin(true);
                try {
                    await this.refresh(refreshToken);
                    return Promise.resolve(true);
                } catch (error) {
                    info(`relogin unsuccessful: ${error}`);
                    return Promise.reject(new AuthError(`relogin unsuccessful: ${error}`, "relogin"));
                } finally {
                    this.authStore.setRelogin(false);
                }
            }
            info("relogin already in progress");
            return Promise.resolve(false);
        }
        info("relogin omitted because no token available");
        return Promise.resolve(false);
    }

    async refresh(refreshToken: string | undefined): Promise<boolean> {
        // TODO: Cookie hat das refresh-token
        if (refreshToken) {
            info("token refresh starts");
            const response = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasGetTokenRessource}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                },
                body: new URLSearchParams({
                    grant_type: "refresh_token",
                    refresh_token: refreshToken,
                    response_type: this.authStore.refreshTokenResponseType,
                    client_id: this.config.wwCustomerManagementClientId,
                    scope: this.authStore.scopes
                })
            });

            if (response.ok) {
                const token = await response.json();
                const user: User = new User();
                if (this.authStore.user) {
                    this.authStore.addUser(
                        this.authStore.user,
                        token.access_token,
                        token.refresh_token,
                        token.expires_in
                    );
                } else {
                    await user.init(token.access_token, token.sub, token.identity_id);
                    this.authStore.addUser(user, token.access_token, token.refresh_token, token.expires_in);
                }
                
                setCookie("token", token.access_token, "Strict");

                info("token refresh successful");
                return Promise.resolve(true);
            } else {
                info(`"token refresh not successful --> ${response.status}: ${response.statusText}`);
                this.authStore.setRefreshToken(undefined);
                return Promise.reject(
                    new AuthError(
                        `token refresh not successful --> ${response.status}: ${response.statusText}`,
                        "refresh"
                    )
                );
            }
        }
        info("refresh omitted because no refresh-token available");
        return Promise.resolve(false);
    }

    async logout(): Promise<boolean> {
        info("logout starts");

        this.authStore.setLogout(true);

        if (this.authStore.user || this.authStore.token) {
            const username = this.authStore.user?.username;
            if (this.authStore.token) {
                try {
                    const response = await fetch(
                        `${this.config.cidaasBaseUrl}${this.config.cidaasEndSessionRessource}?access_token_hint=${this.authStore.token}`,
                        {
                            method: "POST"
                        }
                    );

                    info(`logout response: ${response.status}: ${response.statusText}`);
                    if (response.ok) {
                        info(`logout token revocation successful: ${username}`);
                    } else {
                        info(`logout token revocation not successful: ${username}`);
                        info(`--> ${response.status}: ${response.statusText}`);
                    }
                } catch (e) {
                    // Network/CORS failure: common in local dev where the SPA's
                    // origin isn't on the cidaas tenant's CORS allow-list for
                    // /session/end_session. Proceed with local cleanup; the cidaas
                    // session will time out naturally.
                    info(`logout token revocation failed (network): ${(e as Error).message}`);
                }
            } else {
                info("revocation omitted because no token available");
            }
            this.authStore.removeUser();
            info(`logout successful: ${username}`);
            this.authStore.setLogout(false);
            return Promise.resolve(true);
        } else {
            info("logout omitted because no user or token available");
            this.authStore.setLogout(false);
            return Promise.resolve(false);
        }
    }

    async passwordChange(password: string, newpassword: string): Promise<boolean> {
        info("password change starts");

        const changePasswordBody = {
            client_id: this.config.wwCustomerManagementClientId,
            redirect_url: window.location.origin,
            identityId: this.authStore.user?.identityIdCidaas,
            sub: this.authStore.user?.userIdCidaas,
            old_password: password,
            new_password: newpassword,
            confirm_password: newpassword,
            notify_user: false,
            need_reset_password_on_next_login: false
        };

        const response = await fetch(`${this.config.cidaasBaseUrl}${this.config.cidaasChangePasswordRessource}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.authStore.token}`
            },
            body: JSON.stringify(changePasswordBody)
        });
        info(`password change response: ${response.status}: ${response.statusText}`);

        if (response.ok) {
            info("password change successful");
            return Promise.resolve(true);
        } else {
            info("password change not successful");
            const message = await getErrorMessage(response);
            info(`--> ${message}`);
            return Promise.reject(
                new AuthError(`Beim Ändern des Passworts ist ein Fehler aufgetreten: ${message}`, "password change")
            );
        }
    }
}
